import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AudioButton } from './AudioButton';
import { BookOpen, CheckCircle2, XCircle, Zap, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DailyGrammarChallenge() {
  const [open, setOpen] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const dismissed = sessionStorage.getItem('grammar_challenge_dismissed');
        if (dismissed) return;

        const res = await axios.get(`${API}/grammar-challenge`);

        const data = res.data;
        setChallenge(data.challenge);

        if (data.answered) {
          setAnswered(true);
          setSelected(data.user_answer);
          setResult({
            was_correct: data.was_correct,
            correct_idx: data.challenge.correct,
            explanation_de: '',
            explanation_en: '',
            xp_earned: data.xp_earned
          });
        }

        // Show popup if not yet answered today
        if (!data.answered) {
          setTimeout(() => setOpen(true), 1500);
        }
      } catch (err) {
        // Silently fail — not critical
      }
    };
    fetchChallenge();
  }, []);

  const submitAnswer = async (idx) => {
    if (answered || loading) return;
    setSelected(idx);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/grammar-challenge/answer`, {}, {
        params: { answer: idx }
      });
      setResult(res.data);
      setAnswered(true);
    } catch (err) {
      toast.error('Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem('grammar_challenge_dismissed', 'true');
  };

  if (!challenge) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="grammar-challenge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Daily Grammar Challenge
          </DialogTitle>
          <DialogDescription className="sr-only">Answer today's grammar question</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Topic & Level */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{challenge.level}</Badge>
            <Badge variant="secondary" className="text-xs">{challenge.topic_de || challenge.topic}</Badge>
            <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
              <Zap className="h-3 w-3 text-amber-500" /> {challenge.xp} XP
            </span>
          </div>

          {/* Question */}
          <div className="space-y-1.5">
            <div className="flex items-start gap-2">
              <p className="text-sm font-semibold leading-relaxed flex-1">{challenge.question_de}</p>
              <AudioButton text={challenge.question_de} voice="nova" size="sm" />
            </div>
            <p className="text-xs text-muted-foreground">{challenge.question_en}</p>
          </div>

          {/* Options */}
          <div className="grid gap-2" data-testid="grammar-options">
            {challenge.options?.map((opt, idx) => {
              let style = 'border-border hover:border-primary/50 hover:bg-muted/30';
              if (answered && result) {
                if (idx === result.correct_idx) {
                  style = 'border-emerald-500 bg-emerald-500/10 text-emerald-700';
                } else if (idx === selected && !result.was_correct) {
                  style = 'border-red-500 bg-red-500/10 text-red-600';
                } else {
                  style = 'border-border opacity-50';
                }
              } else if (idx === selected) {
                style = 'border-primary bg-primary/5';
              }

              return (
                <button
                  key={idx}
                  onClick={() => submitAnswer(idx)}
                  disabled={answered || loading}
                  data-testid={`grammar-option-${idx}`}
                  className={`w-full text-left p-2.5 rounded-lg border-2 transition-all text-sm ${style}`}
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-[10px] font-bold mr-2">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>

          {/* Result */}
          {answered && result && (
            <div className={`rounded-lg p-3 text-sm space-y-1.5 ${
              result.was_correct
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : 'bg-red-500/10 border border-red-500/30'
            }`} data-testid="grammar-result">
              <div className="flex items-center gap-2 font-semibold">
                {result.was_correct
                  ? <><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Richtig! +{result.xp_earned} XP</>
                  : <><XCircle className="h-4 w-4 text-red-500" /> Leider falsch</>
                }
              </div>
              {result.explanation_de && (
                <p className="text-xs">{result.explanation_de}</p>
              )}
              {result.explanation_en && (
                <p className="text-xs text-muted-foreground">{result.explanation_en}</p>
              )}
            </div>
          )}

          {/* Close */}
          {answered && (
            <Button className="w-full" size="sm" onClick={handleClose} data-testid="grammar-close-btn">
              Got it!
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
